import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Radio, Sparkles, Loader2, ArrowUpRight, Lock } from "lucide-react";
import { useBilling } from "../context/BillingContext";
import { useAuth } from "../context/AuthContext";
import { ShareTeamButton } from "./ShareTeamButton";

export default function VisionPulseCard() {
  const nav = useNavigate();
  const { handleApiError, canUse, requireUpgrade } = useBilling();
  const locked = !canUse("vision_pulse");
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => {
    if (locked) return;
    try { const { data } = await api.get("/vision-pulse/latest"); setLatest(data && data.id ? data : null); } catch {}
  })(); }, [locked]);

  const generate = async () => {
    if (locked) {
      requireUpgrade({
        message: "Vision Pulse is part of Executive. Upgrade to unlock a weekly board-grade leader update auto-stitched from your musings.",
        feature: "vision_pulse",
      });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vision-pulse/generate", {});
      setLatest(data);
      toast.success("Vision Pulse drafted");
    } catch (e) {
      if (!handleApiError(e)) {
        const msg = e.response?.data?.detail || "Couldn't draft pulse";
        toast.error(typeof msg === "string" ? msg : "Couldn't draft pulse");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="vision-pulse-card">
      <div className="p-6 sm:p-8 border-l-4 border-[#FF3B30]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center">
            {locked ? <Lock className="w-5 h-5 text-[#FFB800]" /> : <Radio className="w-5 h-5 text-[#FFB800]" />}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#FF3B30] font-bold">Vision Pulse · Weekly{locked && " · Executive"}</div>
            <div className="text-2xl sm:text-3xl font-black tracking-tighter mt-1">This week, in one paragraph.</div>
          </div>
        </div>
        <p className="text-sm text-zinc-600 max-w-2xl">
          A board-grade leader update auto-stitched from your last 7 days of musings + reflections — perfect to forward to your team, investors, or family. Drafted by your AI ghostwriter, ready in seconds.
        </p>

        {locked ? (
          <button onClick={generate} data-testid="vision-pulse-generate"
            className="mt-5 bg-zinc-900 text-white px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#0033FF] inline-flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" /> Unlock with Executive
          </button>
        ) : latest ? (
          <div className="mt-5 border border-zinc-200 bg-zinc-50 p-4" data-testid="vision-pulse-preview">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{latest.title}</div>
              <div className="text-xs text-zinc-500 font-mono">{new Date(latest.created_at).toLocaleString()}</div>
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{latest.body}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={()=>nav(`/content/${latest.id}`)} data-testid="vision-pulse-open"
                className="border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2">
                Open in editor <ArrowUpRight className="w-3 h-3" />
              </button>
              <button onClick={generate} disabled={busy} data-testid="vision-pulse-regenerate"
                className="bg-zinc-900 text-white px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0033FF] disabled:opacity-50 inline-flex items-center gap-2">
                {busy ? <><Loader2 className="w-3 h-3 animate-spin"/> Re-drafting…</> : <><Sparkles className="w-3 h-3"/> Draft fresh pulse</>}
              </button>
              <ShareTeamButton key={latest.id} pulseId={latest.id} />
            </div>
          </div>
        ) : (
          <button onClick={generate} disabled={busy} data-testid="vision-pulse-generate"
            className="mt-5 bg-zinc-900 text-white px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#0033FF] disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Drafting your first pulse…</> : <><Sparkles className="w-3.5 h-3.5"/> Generate this week's pulse</>}
          </button>
        )}
        {!locked && <PulseEmailToggle />}
      </div>
    </div>
  );
}

function PulseEmailToggle() {
  const { user, setUser } = useAuth();
  const [on, setOn] = useState(user?.pulse_email_enabled !== false);
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    const next = !on;
    setOn(next); setSaving(true);
    try {
      await api.put("/user/settings", { pulse_email_enabled: next });
      setUser((u) => ({ ...u, pulse_email_enabled: next }));
      toast.success(next ? "Weekly pulse email on — every Monday, 6am Central" : "Weekly pulse email off");
    } catch { setOn(!next); toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-5 pt-4 border-t border-zinc-200 flex items-center justify-between gap-4" data-testid="pulse-email-toggle-row">
      <div>
        <div className="text-sm font-bold">Email me every Monday</div>
        <div className="text-xs text-zinc-500">Your pulse lands in your inbox at 6am US Central.</div>
      </div>
      <button role="switch" aria-checked={on} onClick={toggle} disabled={saving} data-testid="pulse-email-toggle"
        className={`relative w-11 h-6 shrink-0 transition-colors ${on ? "bg-[#0033FF]" : "bg-zinc-300"} disabled:opacity-60`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
      </button>
    </div>
  );
}
